/*
 * Configurator event capture (PostHog HTTP API, project "Neox Store").
 * Per-tab anonymous id in sessionStorage; with analytics consent (Shopify
 * customerPrivacy, fed by the cookie banner) the id and landing attribution
 * persist in localStorage across sessions. Signed-in customers get a separate
 * Shopify-ID person profile only while analytics is allowed. Anonymous IDs are
 * never merged into customer profiles. Exposes window.neoxTrack(event, properties).
 */
(function () {
  const API_KEY = 'phc_Ay4NunU9eqa7E49ck2KBtunbz2Ebk5dFF9sctN3djn2c';
  const CAPTURE_URL = 'https://us.i.posthog.com/i/v0/e/';
  // Liquid supplies only the current signed-in account. Never retain customer
  // names, email addresses or account IDs in browser storage.
  const script = document.querySelector('script[data-neox-analytics]');
  const customerId = script && script.getAttribute('data-customer-id');
  const customer = customerId && /^\d+$/.test(customerId) ? {
    shopify_customer_id: customerId,
    name: script.getAttribute('data-customer-name') || '',
    email: script.getAttribute('data-customer-email') || '',
  } : null;

  function distinctId() {
    return customer && analyticsAllowed() ? 'shopify:' + customer.shopify_customer_id : visitId;
  }

  function analyticsAllowed() {
    try {
      const cp = window.Shopify && window.Shopify.customerPrivacy;
      return !!(cp && typeof cp.analyticsProcessingAllowed === 'function' && cp.analyticsProcessingAllowed());
    } catch (_err) { return false; }
  }

  function newVisitorId() {
    return (window.crypto && crypto.randomUUID) ? crypto.randomUUID() : `v-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }

  let visitId = '';
  try {
    visitId = sessionStorage.getItem('nx_visit_id') || '';
    if (!visitId) {
      visitId = newVisitorId();
      sessionStorage.setItem('nx_visit_id', visitId);
    }
    if (analyticsAllowed()) {
      const stored = localStorage.getItem('nx_visitor_id');
      if (stored) {
        visitId = stored;
      } else {
        localStorage.setItem('nx_visitor_id', visitId);
      }
    }
  } catch (_err) {
    visitId = visitId || newVisitorId();
  }

  // The privacy API or consent can arrive after init. Only consented events
  // adopt the persistent browser ID; no anonymous ID is merged into a customer.
  function adoptPersistentId() {
    try {
      if (analyticsAllowed()) {
        const stored = localStorage.getItem('nx_visitor_id');
        if (stored) visitId = stored;
        else localStorage.setItem('nx_visitor_id', visitId);
      }
    } catch (_err) { /* storage unavailable */ }
  }

  // Ad attribution for the tab: gclid/utm captured from the landing URL and
  // attached to every event, so behaviour can be split by traffic source and
  // add-to-carts uploaded to Google Ads by gclid. A fresh ad click overwrites.
  let attrib = {};
  try {
    const params = new URLSearchParams(location.search);
    const fresh = {};
    ['gclid', 'gbraid', 'wbraid', 'utm_source', 'utm_medium', 'utm_campaign', 'utm_adgroup', 'utm_term', 'utm_matchtype', 'utm_content'].forEach(function (key) {
      const value = params.get(key);
      if (value) fresh[key] = value;
    });
    try {
      const refHost = document.referrer ? new URL(document.referrer).hostname : '';
      if (refHost && refHost !== location.hostname) fresh.landing_referrer = document.referrer;
    } catch (_e) { /* ignore malformed referrer */ }
    if (Object.keys(fresh).length) {
      fresh.landed_at = new Date().toISOString();
      attrib = fresh;
      sessionStorage.setItem('nx_attrib', JSON.stringify(fresh));
      if (analyticsAllowed()) localStorage.setItem('nx_attrib', JSON.stringify(fresh));
    } else {
      attrib = JSON.parse(sessionStorage.getItem('nx_attrib') || 'null')
        || (analyticsAllowed() ? JSON.parse(localStorage.getItem('nx_attrib') || 'null') : null)
        || {};
    }
  } catch (_err) { attrib = {}; }

  // Shopify strips the query string from the order's customerJourneySummary
  // landingPage (verified over 102 orders: zero retained a "?"), so a Google
  // click id reaches the order ONLY as a cart attribute. Underscore-prefixed so
  // it stays off order confirmations and packing slips. With analytics allowed,
  // direct traffic also gets a visitor ID so a purchase can link to its visits.
  // Deduplicate only within this page: a later page may have a fresh cart.
  let cartStamp = '';
  let stampQueue = Promise.resolve();
  function rememberCartLink(linked) {
    for (const store of [sessionStorage, localStorage]) {
      try {
        if (linked) store.setItem('nx_cart_visitor_stamped', 'yes');
        else store.removeItem('nx_cart_visitor_stamped');
      } catch (_e) { /* storage unavailable */ }
    }
  }
  function stampCart() {
    const click = attrib.gclid || attrib.gbraid || attrib.wbraid || '';
    try {
      const visitorId = analyticsAllowed() ? distinctId() : '';
      let mustClear = sessionStorage.getItem('nx_cart_visitor_stamped') === 'yes';
      try { mustClear = mustClear || localStorage.getItem('nx_cart_visitor_stamped') === 'yes'; } catch (_e) { /* storage unavailable */ }
      if (!click && !visitorId && !mustClear) return;
      const attributes = { _nx_visitor_id: visitorId };
      // A direct visit must not erase a previous campaign's cart attribution.
      if (click) Object.assign(attributes, {
        _nx_gclid: attrib.gclid || '',
        _nx_gbraid: attrib.gbraid || '',
        _nx_wbraid: attrib.wbraid || '',
        _nx_landed_at: attrib.landed_at || '',
        _nx_utm_source: attrib.utm_source || '',
        _nx_utm_campaign: attrib.utm_campaign || '',
      });
      const signature = JSON.stringify(attributes);
      if (cartStamp === signature) return;
      cartStamp = signature;
      // Mark before the request, so a consent withdrawal can queue a clear even
      // while the first request is in flight. Serialize to keep that clear last.
      if (visitorId) rememberCartLink(true);
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      stampQueue = stampQueue.then(function () { return fetch(root + 'cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: attributes }),
        keepalive: true,
      }); }).then(function (response) {
        if (!response.ok) throw new Error('Cart attribution update failed');
        rememberCartLink(!!visitorId);
      }).catch(function () { if (cartStamp === signature) cartStamp = ''; });
    } catch (_err) { /* attribution is best-effort, never break the page */ }
  }

  window.neoxTrack = function (event, properties) {
    try {
      const identified = !!(customer && analyticsAllowed());
      const body = JSON.stringify({
        api_key: API_KEY,
        event: event,
        distinct_id: distinctId(),
        properties: Object.assign({
          $lib: 'neox-theme',
          $current_url: location.href,
          path: location.pathname,
          $referrer: document.referrer || null,
        }, attrib, properties || {}, {
          $process_person_profile: identified,
          $is_identified: identified,
        }, identified ? { $set: customer } : {}),
      });
      // keepalive so add-to-cart / pagehide events survive the navigation away.
      fetch(CAPTURE_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: body,
        keepalive: true,
      }).catch(function () {});
    } catch (_err) { /* analytics must never break the page */ }
  };

  let profileCaptured = false;
  let previouslyAllowed = false;
  function refreshConsent() {
    const allowed = analyticsAllowed();
    if (!allowed && previouslyAllowed) {
      // Subsequent anonymous events must not retain a consented order's browser
      // link after consent is withdrawn. Customer profiles are never merged.
      visitId = newVisitorId();
      try { sessionStorage.setItem('nx_visit_id', visitId); } catch (_e) { /* storage unavailable */ }
      try {
        localStorage.removeItem('nx_visitor_id');
        localStorage.removeItem('nx_attrib');
      } catch (_e) { /* storage unavailable */ }
    }
    previouslyAllowed = allowed;
    adoptPersistentId();
    stampCart();
    if (customer && allowed) {
      if (!profileCaptured) {
        window.neoxTrack('customer_identified');
        profileCaptured = true;
      }
    } else profileCaptured = false;
  }
  document.addEventListener('visitorConsentCollected', refreshConsent);
  window.addEventListener('load', refreshConsent);
  refreshConsent();
  // Shopify can load its privacy API after this deferred script. Fail closed
  // for customer identity until the API is available, then refresh cart linkage.
  try {
    if (window.Shopify && !window.Shopify.customerPrivacy && window.Shopify.loadFeatures) {
      window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], function (error) {
        if (!error) refreshConsent();
      });
    }
  } catch (_err) { /* identity stays disabled if the privacy API is unavailable */ }
})();
